import React from "react";
import { Zap } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { waUrl } from "../../config/bdk";

// Custom hero backgrounds provided by the client (dark creative workspace with red accents).
const HERO_BG_DESKTOP = `${process.env.PUBLIC_URL}/img/hero-desktop.jpg`;
const HERO_BG_TABLET = `${process.env.PUBLIC_URL}/img/hero-tablet.jpg`;
const HERO_BG_MOBILE = `${process.env.PUBLIC_URL}/img/hero-movil.jpg`;

const TICKER_ITEMS = [
  "Diseño de páginas web",
  "Landing pages profesionales",
  "Sitios web empresariales",
  "Catálogos, citas y reservaciones",
  "Tiendas en línea",
  "Desarrollo web a la medida",
  "Contacto directo por WhatsApp",
  "Optimización móvil",
  "Formularios de contacto",
  "Mantenimiento web",
  "Cotización personalizada",
];

const TickerGroup = ({ hidden }) => (
  <div className="bdk-ticker__group" aria-hidden={hidden ? "true" : undefined}>
    {TICKER_ITEMS.map((item, i) => (
      <span key={i} className="bdk-ticker__item">
        <Zap strokeWidth={2.5} />
        {item}
      </span>
    ))}
  </div>
);

const Hero = () => {
  useReveal();

  return (
    <section
      id="inicio"
      data-testid="section-hero"
      className="relative w-full overflow-hidden isolate flex flex-col"
      style={{
        background: "#0a0a0d",
        color: "#fff",
        minHeight: "100dvh",
      }}
    >
      {/* Background image (responsive: mobile / tablet / desktop) */}
      <picture aria-hidden className="absolute inset-0 w-full h-full block">
        <source media="(max-width: 640px)" srcSet={HERO_BG_MOBILE} />
        <source media="(max-width: 1024px)" srcSet={HERO_BG_TABLET} />
        <img
          src={HERO_BG_DESKTOP}
          alt=""
          className="w-full h-full object-cover object-right"
          loading="eager"
        />
      </picture>

      {/* Dark gradient overlay (heavier on the left for readable text) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(6,6,8,.92) 0%, rgba(6,6,8,.78) 40%, rgba(6,6,8,.35) 70%, rgba(6,6,8,.1) 100%)",
        }}
      />
      {/* Extra mobile/tablet vertical fade for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 lg:hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,6,8,.88) 0%, rgba(6,6,8,.6) 45%, rgba(6,6,8,.85) 100%)",
        }}
      />

      {/* Red bottom-right accent — hidden on mobile since image already has red */}
      <div
        aria-hidden
        className="absolute pointer-events-none hidden lg:block"
        style={{
          right: "-140px",
          bottom: "-140px",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, rgba(217,0,32,.35), transparent 70%)",
          filter: "blur(0.5px)",
        }}
      />

      {/* Content */}
      <div className="relative flex-1 w-full py-10 sm:py-12 md:py-14 lg:py-14 flex items-center px-5 sm:px-8 lg:pl-[120px] xl:pl-[200px] 2xl:pl-[240px] lg:pr-8">
        <div className="max-w-[860px]">
          {/* Kicker */}
          <div
            className="flex items-center gap-3 bdk-reveal bdk-reveal--up"
            style={{ "--reveal-delay": "0ms" }}
          >
            <span
              className="block"
              style={{
                width: "44px",
                height: "3px",
                background: "var(--red)",
              }}
            />
            <span
              className="font-body font-black uppercase text-[11px] sm:text-[12px]"
              style={{
                color: "var(--red)",
                letterSpacing: "3px",
              }}
            >
              Diseño y desarrollo web
            </span>
          </div>

          {/* Big headline */}
          <h1
            className="mt-6 sm:mt-7 font-display bdk-reveal bdk-reveal--up"
            style={{
              "--reveal-delay": "90ms",
              fontSize: "clamp(38px, 5.6vw, 80px)",
              lineHeight: "0.94",
              letterSpacing: "-0.01em",
            }}
          >
            <span className="block text-white">Páginas web</span>
            <span className="block text-white">profesionales para</span>
            <span className="block" style={{ color: "var(--red)" }}>
              que tu negocio se vea,
            </span>
            <span className="block" style={{ color: "var(--red)" }}>
              se entienda y se venda mejor.
            </span>
          </h1>

          {/* Hook callout — its own visual anchor so it lands even when
              readers skip straight from the H1 to the CTAs. */}
          <div
            className="mt-7 sm:mt-8 inline-flex items-start gap-3 max-w-[640px] p-4 sm:p-5 rounded-[12px] bdk-reveal bdk-reveal--up"
            style={{
              "--reveal-delay": "160ms",
              background: "rgba(217,0,32,.16)",
              border: "1px solid rgba(217,0,32,.55)",
            }}
          >
            <Zap
              size={22}
              strokeWidth={2.5}
              style={{ color: "var(--red)", flexShrink: 0, marginTop: "3px" }}
              aria-hidden
            />
            <p className="font-display text-[19px] sm:text-[22px] md:text-[24px] leading-[1.05] text-white">
              Primero ves tu página.{" "}
              <span style={{ color: "var(--red)" }}>
                Si te gusta, hacemos trato.
              </span>
            </p>
          </div>

          {/* Sub copy */}
          <p
            className="mt-5 sm:mt-6 max-w-[560px] text-[15px] sm:text-[17px] md:text-[18px] leading-[1.55] text-white/80 bdk-reveal bdk-reveal--up"
            style={{ "--reveal-delay": "230ms" }}
          >
            Diseñamos y desarrollamos páginas para presentar tus servicios,
            generar confianza y facilitar que tus clientes te contacten,
            coticen, reserven o compren.
          </p>

          {/* CTAs */}
          <div
            className="mt-8 flex flex-wrap gap-3 bdk-reveal bdk-reveal--up"
            style={{ "--reveal-delay": "310ms" }}
          >
            <a
              href="#contacto"
              data-testid="hero-cta-propuesta"
              onClick={() => window.fbq?.("trackCustom", "ClickCotizarHero")}
              className="bdk-btn bdk-btn--primary bdk-btn--lg"
            >
              Quiero cotizar mi página
            </a>
            <a
              href={waUrl()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              onClick={() => window.fbq?.("trackCustom", "ClickWhatsAppHero")}
              className="bdk-btn bdk-btn--ghost-dark bdk-btn--lg"
            >
              Hablar por WhatsApp
            </a>
          </div>

          <p
            className="mt-4 text-[12px] sm:text-[13px] text-white/50 bdk-reveal bdk-reveal--up"
            style={{ "--reveal-delay": "340ms" }}
          >
            Páginas desde $3,990 MXN + IVA.
          </p>

          {/* Bottom credit */}
          <div
            className="mt-6 sm:mt-8 flex items-start gap-3 bdk-reveal bdk-reveal--up"
            style={{ "--reveal-delay": "360ms" }}
          >
            <span
              className="block mt-2 flex-shrink-0"
              style={{
                width: "38px",
                height: "3px",
                background: "var(--red)",
              }}
            />
            <div>
              <p
                className="font-body font-black uppercase text-[13px] sm:text-[14px] text-white"
                style={{ letterSpacing: "1.5px" }}
              >
                Por{" "}
                <span style={{ color: "var(--red)" }}>BDK</span>
                <span>SISTEMAS</span>
              </p>
              <p className="text-white/60 text-[12px] sm:text-[13px] mt-1">
                14 años impulsando negocios como el tuyo.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom ticker (services we offer, no prices) */}
      <div
        className="bdk-ticker relative w-full"
        aria-label="Servicios que ofrece BDKSISTEMAS"
      >
        <div className="bdk-ticker__track">
          <TickerGroup />
          <TickerGroup hidden />
        </div>
      </div>
    </section>
  );
};

export default Hero;
